import React, { useMemo } from 'react';
import DataTable from '../../../components/data-table/DataTable';
import {
  TableColumn,
  TableConfig,
} from '@leasequery/lq.react-component-library/dist/models';
import { ReportSnapshot } from '../../../models';
import NoDataPanel from '../../../components/no-data-panel/NoDataPanel';
import {
  escapeRegExp,
  extractTextFromHtmlString,
  isCustomAPReport,
  isCatchUpBalanceReconciliationGASBReport,
} from '../../../util';
import { PLEASE_EXPORT_MSG } from '../../../constants/reportSetting';
import { buildTableColumnsFromSnapshot } from './helpers';
import { StyledText } from '@leasequery/lq.react-component-library';
import formatReportResultColumnAccessor from '../../../util/formatReportResultColumnAccessor';
import { useIsFrs102Enabled } from '../../../util/hooks/useIsFrs102Enabled';

interface ReportLeaseTableProps {
  reportSnapshot: ReportSnapshot;
  isSinglePageView?: boolean;
}

const tableConfig: TableConfig = {
  isStickyHeader: false,
  globalFilterDetector: (item: Record<string, string>, value: string) => {
    const columns = Object.values(item);
    const valueInColumn = columns.map((key) => extractTextFromHtmlString(key));

    const results = [];
    valueInColumn.forEach((val) => {
      const includesValue = new RegExp(escapeRegExp(value), 'i').test(val);
      results.push(includesValue);
    });

    return results.includes(true);
  },
};

function ReportLeaseTable({
  reportSnapshot,
  isSinglePageView,
}: ReportLeaseTableProps): JSX.Element {
  const isFrs102Enabled = useIsFrs102Enabled();
  const { results } = reportSnapshot;
  const columns: TableColumn[] = useMemo(
    () => buildTableColumnsFromSnapshot(reportSnapshot, isFrs102Enabled),
    [reportSnapshot, isFrs102Enabled],
  );

  const labels = useMemo(
    () => ({
      itemCount: (
        <>
          <StyledText>Lease Count:&nbsp;</StyledText>
          <StyledText fontWeight="bold">
            {reportSnapshot.totalRows || reportSnapshot.results.length}
          </StyledText>
        </>
      ),
    }),
    [reportSnapshot.results.length, reportSnapshot.totalRows],
  );

  if (isCatchUpBalanceReconciliationGASBReport(reportSnapshot.reportName)) {
    return <NoDataPanel msg={PLEASE_EXPORT_MSG} />;
  }

  results.forEach((result) => {
    Object.keys(result).map(function (keyName) {
      if (keyName) {
        const formattedColumn = formatReportResultColumnAccessor(keyName);
        if (formattedColumn !== keyName) {
          result[formattedColumn] = result[keyName];
          delete result[keyName];
        }
      }
    });
  });

  return columns ? (
    <DataTable
      data={results}
      columns={columns}
      config={tableConfig}
      labels={labels}
      isSinglePageView={isSinglePageView}
    />
  ) : isCustomAPReport(reportSnapshot.reportName) ? (
    <NoDataPanel msg={PLEASE_EXPORT_MSG} />
  ) : (
    <NoDataPanel />
  );
}

export default ReportLeaseTable;
